import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import StatsCard from '../../components/ui/StatsCard';
import PortalDropdown from '../../components/ui/PortalDropdown';
import { projectService } from '../../services/projectService';
import contractService from '../../services/contractService';
import { projectExtrasService } from '../../services/projectExtrasService';
import { invoiceService } from '../../services/invoiceService';
import projectCostService, { computeRowPL } from '../../services/projectCostService';
import divisionsService, { groupCostCodesByDivision, normalizeDivisionCode, extractDivisionCode } from '../../services/divisionsService';
import { expensesService } from '../../services/expensesService';
import extrasBudgetService from '../../services/extrasBudgetService';
import { supabase } from '../../lib/supabase';
import useTranslation from '../../hooks/useTranslation';
import { CSI_DIVISIONS } from '../../constants/csiDivisions';
import { calculateKPIs } from '../../services/budgetKPIService';
import { formatCurrency } from '../../utils/formatCurrency';
import CostCodeNoteModal from './components/CostCodeNoteModal';
import { getCurrentOrgName } from '../../lib/getCurrentOrgId';
import BulkActionBar from '../../components/ui/BulkActionBar';

// Feature flag — set to false to hide the note icon without any DB changes
const ENABLE_COST_CODE_NOTES = true;

// Coloured dot per pricing model — reuses the app convention (T&M = purple)
const PRICING_MODEL_DOT = {
  fixed: 'bg-slate-400',
  labor_material: 'bg-purple-500',
  material_supply: 'bg-amber-500',
};

/**
 * Engagement cell with a hover tooltip breaking down the commitment by contract.
 * Read-only display layer — the amount shown is the already-computed total; the
 * `breakdown` entries reconcile exactly to it. Uses PortalDropdown (portal to
 * document.body) so the panel is never clipped by the table's overflow.
 */
const CommitmentCell = ({ amount, breakdown, t }) => {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const rows = Array.isArray(breakdown) ? breakdown : [];
  const hasDetail = rows.length > 0;

  const modelLabel = (pm) => t(`contracts.pricingModelOptions.${
    pm === 'labor_material' ? 'laborMaterial' : pm === 'material_supply' ? 'materialSupply' : 'fixed'
  }`);

  return (
    <div
      ref={anchorRef}
      className="flex items-center justify-end h-full"
      onMouseEnter={() => hasDetail && setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <span className={hasDetail ? 'border-b border-dotted border-gray-300 cursor-default' : ''}>
        {formatCurrency(amount || 0)}
      </span>
      {hasDetail && (
        <PortalDropdown anchorRef={anchorRef} open={open} onClose={() => setOpen(false)} width={280} align="right">
          <div className="py-1">
            {rows.map((b) => (
              <div key={b?.subcontractId} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${PRICING_MODEL_DOT?.[b?.pricingModel] || 'bg-gray-400'}`} />
                <span className="flex-1 min-w-0 truncate text-gray-700">{b?.subcontractorName || '—'}</span>
                <span className="text-[10px] text-gray-400 flex-shrink-0">{modelLabel(b?.pricingModel)}</span>
                <span className="text-gray-900 font-medium tabular-nums flex-shrink-0">{formatCurrency(b?.amount || 0)}</span>
              </div>
            ))}
            {rows.length > 1 && (
              <div className="flex items-center gap-2 px-3 py-1.5 mt-0.5 border-t border-gray-100 text-xs">
                <span className="flex-1 text-gray-500">{t('budget.tooltips.commitmentTotal')}</span>
                <span className="text-gray-900 font-semibold tabular-nums">{formatCurrency(amount || 0)}</span>
              </div>
            )}
          </div>
        </PortalDropdown>
      )}
    </div>
  );
};

const BudgetPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const initialProject = location?.state?.project;

  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(initialProject || null);
  const [contracts, setContracts] = useState([]);
  const [extras, setExtras] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [activeTab, setActiveTab] = useState('construction');
  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');

  // Add this block - missing state declarations that are used before their original declaration
  const [expandedDivisions, setExpandedDivisions] = useState({});
  const [deleteCheckLoading, setDeleteCheckLoading] = useState(false);

  // Cost-code note modal state
  const [noteModal, setNoteModal] = useState(null); // { id, costCode, description } | null
  // Map of costCodeId -> estimationNote (string | null) for icon color
  const [costCodeNotes, setCostCodeNotes] = useState({});
  const [orgName, setOrgName] = useState('');

  // ── Create Contract selection state ──────────────────────────────────────────
  const [selectedCostCodeIds, setSelectedCostCodeIds] = useState(new Set());
  const [createContractLoading, setCreateContractLoading] = useState(false);
  const [contractToast, setContractToast] = useState(null); // { type: 'success'|'error', message: string }

  // Add this block - missing helper function and variable declarations
  const tabs = [
    { id: 'construction', label: t('budget.tabs.construction') || 'Construction' },
    { id: 'general', label: t('budget.tabs.general') || 'General' },
    { id: 'admin', label: t('budget.tabs.admin') || 'Admin' },
    { id: 'extras', label: t('budget.tabs.extras') || 'Extras & Credits' }
  ];

  const filterOptions = [
    { id: 'all', label: t('budget.filters.all') || 'All' },
    { id: 'over-budget', label: t('budget.filters.overBudget') || 'Over Budget' },
    { id: 'under-budget', label: t('budget.filters.underBudget') || 'Under Budget' },
    { id: 'zero-variance', label: t('budget.filters.zeroVariance') || 'Zero Variance' },
    { id: 'high-variance', label: t('budget.filters.highVariance') || 'High Variance' }
  ];

  // remove local formatCurrency — using centralized util from src/utils/formatCurrency.js

  const isDivisionExpanded = (divisionCode) => {
    return expandedDivisions?.[divisionCode] === true;
  };

  const toggleDivisionExpanded = (divisionCode) => {
    setExpandedDivisions(prev => ({
      ...prev,
      [divisionCode]: !isDivisionExpanded(divisionCode)
    }));
  };

  // Add this block - state declarations for divisions and costs before toggleAllDivisions
  const [constructionCosts, setConstructionCosts] = useState([]);
  const [generalCosts, setGeneralCosts] = useState([]);
  const [adminCosts, setAdminCosts] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [extrasCosts, setExtrasCosts] = useState([]);

  // Expandable extras state
  const [expandedExtras, setExpandedExtras] = useState({});
  const [extraLinesCache, setExtraLinesCache] = useState({});
  const [extraLinesLoading, setExtraLinesLoading] = useState({});

  // Add Division modal state
  const [showAddDivisionModal, setShowAddDivisionModal] = useState(false);
  const [addDivisionForm, setAddDivisionForm] = useState({ number: '', title: '' });
  const [addDivisionLoading, setAddDivisionLoading] = useState(false);
  const [addDivisionError, setAddDivisionError] = useState('');
  const [divisionSearch, setDivisionSearch] = useState('');
  const [divisionToast, setDivisionToast] = useState('');
  const [selectedDivisions, setSelectedDivisions] = useState([]);

  // Add Cost Code modal state (inline division context)
  const [showAddCostCodeModal, setShowAddCostCodeModal] = useState(false);
  const [addCostCodeDivision, setAddCostCodeDivision] = useState(null); // { code, name }
  const [addCostCodeForm, setAddCostCodeForm] = useState({ costCode: '', description: '', budget: '', forecast: '' });
  const [addCostCodeLoading, setAddCostCodeLoading] = useState(false);

  // Add Cost Code modal state (general / admin tabs — no division)
  const [showAddGeneralCostCodeModal, setShowAddGeneralCostCodeModal] = useState(false);
  const [addGeneralCostCodeForm, setAddGeneralCostCodeForm] = useState({ costCode: '', description: '' });
  const [addGeneralCostCodeLoading, setAddGeneralCostCodeLoading] = useState(false);

  // Add this block - Edit Division modal state
  const [showEditDivisionModal, setShowEditDivisionModal] = useState(false);
  const [editingDivision, setEditingDivision] = useState(null);
  const [editDivisionForm, setEditDivisionForm] = useState({ number: '', title: '' });
  const [editDivisionLoading, setEditDivisionLoading] = useState(false);
  const [editDivisionError, setEditDivisionError] = useState('');

  // Add this block - Delete Division modal state
  const [showDeleteDivisionBlockedModal, setShowDeleteDivisionBlockedModal] = useState(false);
  const [blockedDivisionCostCount, setBlockedDivisionCostCount] = useState(0);
  const [openDivisionMenu, setOpenDivisionMenu] = useState(null);
  const divisionMenuBtnRefs = useRef({});

  const [showDeleteDivisionConfirmModal, setShowDeleteDivisionConfirmModal] = useState(false);
  const [pendingDeleteDivision, setPendingDeleteDivision] = useState(null);
  const [deletingDivision, setDeletingDivision] = useState(false);

  // Case A: normal confirm
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  // Case B: blocked (actual > 0 + linked records)
  const [showDeleteBlockedModal, setShowDeleteBlockedModal] = useState(false);
  const [deleteBlockedInfo, setDeleteBlockedInfo] = useState({ contracts: [], invoices: [], expenses: [] });

  // Case C: warning (actual > 0 but no linked records)
  const [showDeleteWarningModal, setShowDeleteWarningModal] = useState(false);
  const [pendingDeleteWarningId, setPendingDeleteWarningId] = useState(null);

  const toggleExtraExpanded = async (extraId) => {
    const isCurrentlyExpanded = expandedExtras?.[extraId];
    setExpandedExtras(prev => ({ ...prev, [extraId]: !isCurrentlyExpanded }));

    // Fetch lines if not cached yet
    if (!isCurrentlyExpanded && !extraLinesCache?.[extraId]) {
      setExtraLinesLoading(prev => ({ ...prev, [extraId]: true }));
      try {
        const { data: extraLines, error } = await supabase?.from('project_extra_lines')?.select(`
            id,
            description,
            amount,
            is_internal,
            subcontract_id,
            subcontracts (
              id,
              subcontractors (
                company_name
              )
            )
          `)?.eq('extra_id', extraId);

        // Engagement REEL par sous-traitant : ses avenants envoyes ou signes pour
        // cet extra, avec leur signe. Meme regle que la ligne parente
        // (extrasBudgetService), pour que la somme des sous-lignes lui soit egale.
        const { data: cos, error: coError } = await supabase?.from('subcontract_change_orders')?.select(`
            subcontract_id,
            amount,
            subcontracts (
              id,
              subcontractors (
                company_name
              )
            )
          `)?.eq('source_extra_id', extraId)?.in('status', ['signed', 'sent']);

        if (!error && !coError) {
          // Une sous-ligne par sous-traitant (ses lignes d'extra sont regroupees),
          // plus une sous-ligne pour le travail interne, qui n'a jamais d'avenant.
          const rows = {};
          for (const l of extraLines || []) {
            const internal = l?.is_internal || !l?.subcontract_id;
            const key = internal ? 'internal' : l?.subcontract_id;
            if (!rows?.[key]) {
              rows[key] = { id: key, is_internal: internal, subcontracts: internal ? null : l?.subcontracts, commitment: 0 };
            }
          }
          for (const co of cos || []) {
            const key = co?.subcontract_id;
            if (!rows?.[key]) {
              rows[key] = { id: key, is_internal: false, subcontracts: co?.subcontracts, commitment: 0 };
            }
            rows[key].commitment += parseFloat(co?.amount) || 0;
          }
          setExtraLinesCache(prev => ({ ...prev, [extraId]: Object.values(rows) }));
        }
      } catch (err) {
        console.error('Error fetching extra lines:', err);
        setExtraLinesCache(prev => ({ ...prev, [extraId]: [] }));
      } finally {
        setExtraLinesLoading(prev => ({ ...prev, [extraId]: false }));
      }
    }
  };

  // Filter extrasCosts using approved IDs from extras (status lives in extras, not extrasCosts)
  const approvedExtraIds = extras?.filter(e => e?.status === 'approved')?.map(e => e?.id) || [];
  const approvedExtrasCosts = extrasCosts?.filter(e => approvedExtraIds?.includes(e?.id)) || [];

  const handleUpdateForecast = (id, value, persist = false) => {
    setExtrasCosts(prev =>
      prev?.map(item => {
        if (item?.id !== id) return item;
        const forecast = Number(value) || 0;
        const pl = item?.initialValue - (item?.commitment + item?.expenses + forecast);
        const plPercent = item?.initialValue !== 0 ? (pl / Math.abs(item?.initialValue)) * 100 : 0;
        return { ...item, forecast, pl, plPercent };
      })
    );
    if (persist) {
      extrasBudgetService?.updateForecast(id, Number(value) || 0);
    }
  };

  const toggleAllDivisions = () => {
    const allExpanded = divisions?.length > 0 &&
      divisions?.every(div => isDivisionExpanded(div?.code));
    if (allExpanded) {
      const collapsed = {};
      divisions?.forEach(div => {
        collapsed[div.code] = false;
      });
      setExpandedDivisions(collapsed);
    } else {
      const expanded = {};
      divisions?.forEach(div => {
        expanded[div.code] = true;
      });
      setExpandedDivisions(expanded);
    }
  };

  // Shared color helpers
  // getProfitColor: colors the P/L dollar value based on sign
  const getProfitColor = (pl) => {
    if (pl > 0) return "text-green-600";
    if (pl < 0) return "text-red-500";
    return "text-gray-400";
  };

  // getMarginColor: colors the P/L % based on margin thresholds
  const getMarginColor = (percent) => {
    if (percent >= 20) return "text-green-600";
    if (percent >= 5) return "text-orange-500";
    return "text-red-500";
  };

  const calcGroupSubtotals = (rows) => {
    const budget = rows?.reduce((sum, r) => sum + (r?.budget || 0), 0) || 0;
    const actual = rows?.reduce((sum, r) => sum + (r?.actual || 0), 0) || 0;
    const commitmentActual = rows?.reduce((sum, r) => sum + (r?.commitmentActual || 0), 0) || 0;
    const expensesActual = rows?.reduce((sum, r) => sum + (r?.expensesActual || 0), 0) || 0;
    const forecast = rows?.reduce((sum, r) => sum + (r?.forecast || 0), 0) || 0;
    const { pl, plPercent } = computeRowPL({ budget, actual, forecast });
    return { budget, actual, commitmentActual, expensesActual, forecast, pl, plPercent };
  };

  // ── Toggle cost code selection ────────────────────────────────────────────────
  const toggleCostCodeSelection = (itemId) => {
    setSelectedCostCodeIds(prev => {
      const next = new Set(prev);
      if (next?.has(itemId)) {
        next?.delete(itemId);
      } else {
        next?.add(itemId);
      }
      return next;
    });
  };

  // ── Handle Create Contract action ─────────────────────────────────────────────
  const handleCreateContracts = async () => {
    if (selectedCostCodeIds?.size === 0 || createContractLoading) return;
    setCreateContractLoading(true);
    try {
      // Gather selected cost code objects
      const selectedItems = constructionCosts?.filter(c => selectedCostCodeIds?.has(c?.id));
      const inserts = selectedItems?.map(item => ({
        project_id: id,
        project_cost_id: item?.id,
        subcontractor_id: null,
        contract_value: null,
        status: 'to_assign',
        document_type: 'contract',
        pricing_model: 'fixed',
        name: 'To assign',
        company: 'To assign',
        trade: 'To assign',
        notes: null
      }));

      const { error } = await supabase?.from('subcontracts')?.insert(inserts);
      if (error) throw error;

      const count = inserts?.length;
      const successMsg = t('budget.createContract.success')?.replace('{count}', count);
      setContractToast({ type: 'success', message: successMsg });
      setTimeout(() => setContractToast(null), 3500);

      // Clear selection
      setSelectedCostCodeIds(new Set());

      // Refresh construction costs to reflect updated commitment
      const updatedCosts = await projectCostService?.getByProjectAndCategory(id, 'construction');
      setConstructionCosts(updatedCosts || []);
    } catch (err) {
      console.error('Error creating contracts:', err);
      setContractToast({ type: 'error', message: t('budget.createContract.error') });
      setTimeout(() => setContractToast(null), 4000);
    } finally {
      setCreateContractLoading(false);
    }
  };

  const handleCellEdit = async (itemId, field, value, isLive = false) => {
    const updateState = (prev) =>
      prev?.map(item => {
        if (item?.id !== itemId) return item;
        const updated = { ...item, [field]: field === 'budget' || field === 'forecast' ? Number(value) || 0 : value };
        if (field === 'budget' || field === 'forecast') {
          const { pl, plPercent } = computeRowPL({ budget: updated?.budget, actual: updated?.actual, forecast: updated?.forecast });
          return { ...updated, pl, plPercent };
        }
        return updated;
      });
    setConstructionCosts(updateState);
    setGeneralCosts(updateState);
    setAdminCosts(updateState);
    if (!isLive && (field === 'budget' || field === 'forecast' || field === 'costCode' || field === 'description')) {
      try {
        await projectCostService?.update(itemId, { [field === 'costCode' ? 'cost_code' : field]: field === 'budget' || field === 'forecast' ? Number(value) || 0 : value });
      } catch (err) {
        console.error('Error updating cell:', err);
      }
    }
  };

  const handleDeleteRow = async (itemId, costCode) => {
    setDeleteCheckLoading(true);
    try {
      const [contractsRes, invoicesRes, expensesRes] = await Promise.all([
        supabase?.from('subcontracts')?.select('id, name, contract_value')?.eq('project_cost_id', itemId),
        supabase?.from('invoices')?.select('id, invoice_number, amount')?.eq('project_cost_id', itemId),
        supabase?.from('expenses')?.select('id, description, amount')?.eq('project_cost_id', itemId)
      ]);
      const linkedContracts = contractsRes?.data || [];
      const linkedInvoices = invoicesRes?.data || [];
      const linkedExpenses = expensesRes?.data || [];
      const hasLinked = linkedContracts?.length > 0 || linkedInvoices?.length > 0 || linkedExpenses?.length > 0;
      const costItem = [...constructionCosts, ...generalCosts, ...adminCosts]?.find(c => c?.id === itemId);
      const hasActual = (costItem?.actual || 0) > 0;
      if (hasLinked) {
        setDeleteBlockedInfo({ contracts: linkedContracts?.map(c => ({ name: c?.name, value: c?.contract_value })), invoices: linkedInvoices?.map(inv => ({ number: inv?.invoice_number, amount: inv?.amount })), expenses: linkedExpenses?.map(exp => ({ description: exp?.description, amount: exp?.amount })) });
        setShowDeleteBlockedModal(true);
      } else if (hasActual) {
        setPendingDeleteWarningId(itemId);
        setShowDeleteWarningModal(true);
      } else {
        setPendingDeleteId(itemId);
        setShowDeleteConfirmModal(true);
      }
    } catch (err) {
      console.error('Error checking delete:', err);
    } finally {
      setDeleteCheckLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteId) return;
    try {
      await projectCostService?.remove(pendingDeleteId);
      setConstructionCosts(prev => prev?.filter(c => c?.id !== pendingDeleteId));
      setGeneralCosts(prev => prev?.filter(c => c?.id !== pendingDeleteId));
      setAdminCosts(prev => prev?.filter(c => c?.id !== pendingDeleteId));
      setShowDeleteConfirmModal(false);
      setPendingDeleteId(null);
    } catch (err) {
      console.error('Error deleting cost code:', err);
    }
  };

  const handleConfirmDeleteAnyway = async () => {
    if (!pendingDeleteWarningId) return;
    try {
      await projectCostService?.remove(pendingDeleteWarningId);
      setConstructionCosts(prev => prev?.filter(c => c?.id !== pendingDeleteWarningId));
      setGeneralCosts(prev => prev?.filter(c => c?.id !== pendingDeleteWarningId));
      setAdminCosts(prev => prev?.filter(c => c?.id !== pendingDeleteWarningId));
      setShowDeleteWarningModal(false);
      setPendingDeleteWarningId(null);
    } catch (err) {
      console.error('Error deleting cost code:', err);
    }
  };

  const handleOpenAddCostCodeModal = (division, e) => {
    e?.stopPropagation();
    setAddCostCodeDivision(division);
    setAddCostCodeForm({ costCode: '', description: '', budget: '', forecast: '' });
    setShowAddCostCodeModal(true);
  };

  const getFilteredCostCodes = () => {
    const codes = getCurrentCostCodes();
    if (activeFilter === 'all') return codes;
    return codes?.filter(item => {
      const { pl, plPercent } = computeRowPL({ budget: item?.budget || 0, actual: item?.actual || 0, forecast: item?.forecast || 0 });
      if (activeFilter === 'over-budget') return pl < 0;
      if (activeFilter === 'under-budget') return pl > 0;
      if (activeFilter === 'zero-variance') return pl === 0;
      if (activeFilter === 'high-variance') return Math.abs(plPercent) > 20;
      return true;
    });
  };

  const applyFilter = (filterId) => {
    setActiveFilter(filterId);
    setFilterDropdownOpen(false);
  };

  const handleSaveAddCostCode = async () => {
    if (!addCostCodeForm?.description || addCostCodeLoading) return;
    setAddCostCodeLoading(true);
    try {
      await projectCostService?.create({
        projectId: id,
        category: 'construction',
        costCode: addCostCodeForm?.costCode,
        description: addCostCodeForm?.description,
        budget: parseFloat(addCostCodeForm?.budget) || 0,
        forecast: parseFloat(addCostCodeForm?.forecast) || 0,
        divisionCode: addCostCodeDivision?.code || null
      });
      const updatedCosts = await projectCostService?.getByProjectAndCategory(id, 'construction');
      setConstructionCosts(updatedCosts || []);
      setShowAddCostCodeModal(false);
      setAddCostCodeDivision(null);
      setAddCostCodeForm({ costCode: '', description: '', budget: '', forecast: '' });
    } catch (err) {
      console.error('Error saving cost code:', err);
    } finally {
      setAddCostCodeLoading(false);
    }
  };

  const handleSaveGeneralAdminCostCode = async () => {
    if (!addGeneralCostCodeForm?.description || addGeneralCostCodeLoading) return;
    setAddGeneralCostCodeLoading(true);
    try {
      await projectCostService?.create({
        projectId: id,
        category: activeTab,
        costCode: addGeneralCostCodeForm?.costCode,
        description: addGeneralCostCodeForm?.description,
        budget: 0,
        forecast: 0,
        divisionCode: null
      });
      const updatedCosts = await projectCostService?.getByProjectAndCategory(id, activeTab);
      if (activeTab === 'general') setGeneralCosts(updatedCosts || []);
      if (activeTab === 'admin') setAdminCosts(updatedCosts || []);
      setShowAddGeneralCostCodeModal(false);
      setAddGeneralCostCodeForm({ costCode: '', description: '' });
    } catch (err) {
      console.error('Error saving cost code:', err);
    } finally {
      setAddGeneralCostCodeLoading(false);
    }
  };

  const handleSaveEditDivision = async () => {
    if (!editDivisionForm?.number || !editDivisionForm?.title || editDivisionLoading) return;
    const normalizedCode = normalizeDivisionCode(editDivisionForm?.number);
    if (!normalizedCode) {
      setEditDivisionError(t('budget.addDivision.errorNoDigit'));
      return;
    }
    try {
      setEditDivisionLoading(true);
      const orderIndex = parseInt(normalizedCode, 10) || 0;
      await divisionsService?.update(editingDivision?.id, { code: normalizedCode, name: editDivisionForm?.title, order_index: orderIndex });
      const updatedDivisions = await divisionsService?.getByProjectId(id);
      const normalizedDivisions = (updatedDivisions || [])?.map(div => ({
        ...div,
        code: normalizeDivisionCode(div?.code) ?? div?.code
      }));
      setDivisions(normalizedDivisions);
      setShowEditDivisionModal(false);
      setEditingDivision(null);
      setEditDivisionForm({ number: '', title: '' });
      setEditDivisionError('');
    } catch (error) {
      console.error('Error updating division:', error);
      setEditDivisionError(t('budget.division.errorUpdating'));
    } finally {
      setEditDivisionLoading(false);
    }
  };

  const renderCostCodeRow = (item, isIndented) => {
    const budget = item?.budget || 0;
    const actual = item?.actual || 0;
    const commitmentActual = item?.commitmentActual || 0;
    const expensesActual = item?.expensesActual || 0;
    const forecast = item?.forecast || 0;
    const { pl, plPercent } = computeRowPL({ budget, actual, forecast });

    // Note icon color: orange if note exists, gray otherwise
    const hasNote = costCodeNotes?.[item?.id] != null
      ? Boolean(costCodeNotes?.[item?.id])
      : Boolean(item?.estimationNote);

    const isSelected = selectedCostCodeIds?.has(item?.id);

    return (
      <tr key={item?.id} className={`border-b border-gray-100 hover:bg-gray-50 transition-colors h-[34px] ${isSelected ? 'bg-orange-50/40' : ''}`}>
        {/* Cost code column — checkbox inline before cost code number in construction tab */}
        <td className={`align-middle text-xs text-gray-700 font-normal ${isIndented ? 'pl-10' : 'pl-3'} pr-2`} style={{ width: '10%' }}>
          <div className="flex items-center gap-2 h-full">
            {activeTab === 'construction' && (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggleCostCodeSelection(item?.id)}
                onClick={(e) => e?.stopPropagation()}
                className="w-3.5 h-3.5 rounded border-gray-300 text-primary focus:ring-2 focus:ring-primary/20 focus:ring-offset-0 cursor-pointer accent-primary flex-shrink-0"
              />
            )}
          {editingCell?.id === item?.id && editingCell?.field === 'costCode' ? (
            <input
              type="text"
              value={item?.costCode || ''}
              onChange={(e) => handleCellEdit(item?.id, 'costCode', e?.target?.value)}
              onBlur={() => setEditingCell(null)}
              autoFocus
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          ) : (
            <span onClick={() => setEditingCell({ id: item?.id, field: 'costCode' })} className="cursor-pointer whitespace-nowrap overflow-hidden text-ellipsis">
              {item?.costCode || '—'}
            </span>
          )}
          </div>
        </td>
        <td className="px-4 align-middle text-xs text-gray-600" style={{ width: '27%' }}>
          <div className="flex items-center h-full">
          {editingCell?.id === item?.id && editingCell?.field === 'description' ? (
            <input
              type="text"
              value={item?.description || ''}
              onChange={(e) => handleCellEdit(item?.id, 'description', e?.target?.value)}
              onBlur={() => setEditingCell(null)}
              autoFocus
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          ) : (
            <span onClick={() => setEditingCell({ id: item?.id, field: 'description' })} className="cursor-pointer whitespace-nowrap overflow-hidden text-ellipsis block">
              {item?.description || '—'}
            </span>
          )}
          </div>
        </td>
        <td className="px-4 align-middle text-xs text-gray-900 text-right font-medium" style={{ width: '12%' }}>
          <div className="flex items-center justify-end h-full">
          {editingCell?.id === item?.id && editingCell?.field === 'budget' ? (
            <input
              type="text"
              value={editingValue}
              onChange={(e) => {
                const raw = e?.target?.value?.replace(/[^0-9.]/g, '');
                setEditingValue(raw);
              }}
              onBlur={() => {
                const parsed = parseFloat(editingValue) || 0;
                handleCellEdit(item?.id, 'budget', String(parsed));
                setEditingCell(null);
              }}
              autoFocus
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded text-right focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          ) : (
            <span onClick={() => { setEditingCell({ id: item?.id, field: 'budget' }); setEditingValue(item?.budget != null ? String(item?.budget) : ''); }} className="cursor-pointer">
              {formatCurrency(budget || 0)}
            </span>
          )}
          </div>
        </td>
        <td className="px-4 align-middle text-xs text-gray-500 text-right" style={{ width: '10%' }}>
          <CommitmentCell amount={commitmentActual} breakdown={item?.commitmentBreakdown} t={t} />
        </td>
        <td className="px-4 align-middle text-xs text-gray-500 text-right" style={{ width: '10%' }}>
          <div className="flex items-center justify-end h-full">
            {formatCurrency(expensesActual || 0)}
          </div>
        </td>
        <td className="px-4 align-middle text-xs text-right">
          <div className="flex items-center justify-end h-full">
          <input
            type="text"
            inputMode="numeric"
            value={item?.forecast ?? ""}
            onFocus={(e) => e?.target?.select()}
            onChange={(e) => handleCellEdit(item?.id, 'forecast', e?.target?.value, true)}
            onBlur={(e) => handleCellEdit(item?.id, 'forecast', e?.target?.value)}
            className="w-20 h-6 block ml-auto text-right text-xs border border-gray-200 rounded px-1 leading-none outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white"
          />
          </div>
        </td>
        <td className="px-4 align-middle text-xs text-right">
          <div className="flex items-center justify-end h-full">
            <span className={`text-xs font-semibold ${getProfitColor(pl)}`}>
              {formatCurrency(Math.abs(pl))}
            </span>
          </div>
        </td>
        <td className="px-4 align-middle text-xs text-right">
          <div className="flex items-center justify-end h-full">
            <span className={`text-xs font-semibold ${getMarginColor(plPercent)}`}>
              {plPercent?.toFixed(1)}%
            </span>
          </div>
        </td>
        <td className="px-4 align-middle text-center" style={{ width: '5%' }}>
          <div className="flex items-center justify-center gap-1 h-full">
            {ENABLE_COST_CODE_NOTES && (
              <button
                onClick={() => setNoteModal({ id: item?.id, costCode: item?.costCode, description: item?.description })}
                className={`transition-colors ${hasNote ? 'text-primary hover:text-primary-dark' : 'text-gray-400 hover:text-gray-600'}`}
                title={hasNote ? t('costCodeNote.iconTooltipEdit') : t('costCodeNote.iconTooltipAdd')}
              >
                <Icon name="FileText" size={14} />
              </button>
            )}
            <button
              onClick={() => handleDeleteRow(item?.id, item?.costCode)}
              disabled={deleteCheckLoading}
              className="text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
              title={t('budget.tooltips.deleteCostCode')}
            >
              <Icon name="Trash2" size={14} />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [projectData, contracts, extras, invoices, constructionCosts, generalCosts, adminCosts, divisionsData, expensesData, extrasData] = await Promise.all([
          projectService?.getById(id),
          contractService?.getByProjectId(id),
          projectExtrasService?.getByProjectId(id),
          invoiceService?.getByProjectId(id),
          projectCostService?.getByProjectAndCategory(id, 'construction'),
          projectCostService?.getByProjectAndCategory(id, 'general'),
          projectCostService?.getByProjectAndCategory(id, 'admin'),
          divisionsService?.getByProjectId(id),
          expensesService?.getByProject(id),
          extrasBudgetService?.getByProject(id)
        ]);

        setProject(projectData);
        setContracts(contracts || []);
        setExtras(extras || []);
        setInvoices(invoices || []);
        setConstructionCosts(constructionCosts || []);
        setGeneralCosts(generalCosts || []);
        setAdminCosts(adminCosts || []);
        // Normalize division codes in-memory to handle legacy data
        const normalizedDivisions = (divisionsData || [])?.map(div => ({
          ...div,
          code: normalizeDivisionCode(div?.code) ?? div?.code
        }));
        setDivisions(normalizedDivisions);
        setExpenses(expensesData || []);
        setExtrasCosts(extrasData || []);

      } catch (error) {
        console.error('Error fetching budget data:', error);
      } finally {
        setLoading(false);
      }
    };
    if (id) {
      fetchData();
    }
  }, [id]);

  useEffect(() => {
    getCurrentOrgName()?.then(name => {
      if (name) setOrgName(name);
    });
  }, []);

  const getCurrentCostCodes = () => {
    switch (activeTab) {
      case 'construction': return constructionCosts;
      case 'general': return generalCosts;
      case 'admin': return adminCosts;
      default: return constructionCosts;
    }
  };

  const setCurrentCostCodes = (newCodes) => {
    switch (activeTab) {
      case 'construction': setConstructionCosts(newCodes); break;
      case 'general': setGeneralCosts(newCodes); break;
      case 'admin': setAdminCosts(newCodes); break;
    }
  };

  const kpis = calculateKPIs({
    constructionCosts,
    generalCosts,
    adminCosts,
    extras,
    // extrasCosts is the financial dataset (commitment, expenses, forecast)
    // extras is used only for status and contract value
    extrasCosts
  });

  const handleAddDivision = async () => {
    setAddDivisionError('');
    const normalizedCode = normalizeDivisionCode(addDivisionForm?.number);
    if (!normalizedCode) {
      setAddDivisionError(t('budget.errors.divisionNeedsDigit'));
      return;
    }
    if (!addDivisionForm?.title) return;
    try {
      setAddDivisionLoading(true);
      // Calculate order_index from normalized code (numeric value)
      const orderIndex = parseInt(normalizedCode, 10) || 0;
      await divisionsService?.create({
        projectId: id,
        code: normalizedCode,
        name: addDivisionForm?.title,
        orderIndex
      });
      // Re-fetch divisions and re-render
      const updatedDivisions = await divisionsService?.getByProjectId(id);
      const normalizedDivisions = (updatedDivisions || [])?.map(div => ({
        ...div,
        code: normalizeDivisionCode(div?.code) ?? div?.code
      }));
      setDivisions(normalizedDivisions);
      const updatedCosts = await projectCostService?.getByProjectAndCategory(id, 'construction');
      setConstructionCosts(updatedCosts || []);
      setShowAddDivisionModal(false);
      setAddDivisionForm({ number: '', title: '' });
      setAddDivisionError('');
      setDivisionSearch('');
      setDivisionToast('');
    } catch (error) {
      if (error?.code === '23505') {
        setAddDivisionError(t('budget.addDivision.errorAlreadyExists', { code: normalizedCode }));
      } else {
        console.error('Error adding division:', error);
        setAddDivisionError(t('budget.addDivision.errorCreating'));
      }
    } finally {
      setAddDivisionLoading(false);
    }
  };

  const handleSelectCSIDivision = async (division) => {
    const normalizedCode = normalizeDivisionCode(division?.number);
    // Check for duplicate
    const alreadyExists = divisions?.some(d => normalizeDivisionCode(d?.code) === normalizedCode);
    if (alreadyExists) {
      setDivisionToast(t('budget.addDivision.toastAlreadyAdded'));
      setTimeout(() => setDivisionToast(''), 3000);
      return;
    }
    try {
      setAddDivisionLoading(true);
      const orderIndex = parseInt(normalizedCode, 10) || 0;
      await divisionsService?.create({
        projectId: id,
        code: normalizedCode,
        name: division?.title,
        orderIndex
      });
      const updatedDivisions = await divisionsService?.getByProjectId(id);
      const normalizedDivisions = (updatedDivisions || [])?.map(div => ({
        ...div,
        code: normalizeDivisionCode(div?.code) ?? div?.code
      }));
      setDivisions(normalizedDivisions);
      const updatedCosts = await projectCostService?.getByProjectAndCategory(id, 'construction');
      setConstructionCosts(updatedCosts || []);
      setShowAddDivisionModal(false);
      setDivisionSearch('');
      setDivisionToast('');
    } catch (error) {
      if (error?.code === '23505') {
        setDivisionToast(t('budget.addDivision.toastAlreadyAdded'));
        setTimeout(() => setDivisionToast(''), 3000);
      } else {
        console.error('Division creation failed:', error);
        setDivisionToast(error?.message || t('budget.addDivision.errorAddFailed'));
        setTimeout(() => setDivisionToast(''), 5000);
      }
    } finally {
      setAddDivisionLoading(false);
    }
  };

  const toggleDivision = (division) => {
    setSelectedDivisions(prev => {
      const exists = prev?.find(d => d?.number === division?.number);
      if (exists) {
        return prev?.filter(d => d?.number !== division?.number);
      }
      return [...prev, division];
    });
  };

  const handleAddSelectedDivisions = async () => {
    if (selectedDivisions?.length === 0) return;
    setAddDivisionLoading(true);
    let skipped = 0;
    for (const division of selectedDivisions) {
      const normalizedCode = normalizeDivisionCode(division?.number);
      // Only check project-specific divisions (not global ones) to avoid false positives
      const alreadyExists = divisions?.some(d => d?.projectId === id && normalizeDivisionCode(d?.code) === normalizedCode);
      if (alreadyExists) {
        skipped++;
        continue;
      }
      try {
        const orderIndex = parseInt(normalizedCode, 10) || 0;
        await divisionsService?.create({
          projectId: id,
          code: normalizedCode,
          name: division?.title,
          orderIndex
        });
      } catch (error) {
        console.error('Division creation failed:', error);
        if (error?.code === '23505') {
          skipped++;
        } else {
          setDivisionToast(error?.message || t('budget.addDivision.errorAddFailed'));
          setTimeout(() => setDivisionToast(''), 5000);
          setAddDivisionLoading(false);
          return;
        }
      }
    }
    // Re-fetch after all inserts
    const updatedDivisions = await divisionsService?.getByProjectId(id);
    const normalizedDivisions = (updatedDivisions || [])?.map(div => ({
      ...div,
      code: normalizeDivisionCode(div?.code) ?? div?.code
    }));
    setDivisions(normalizedDivisions);
    const updatedCosts = await projectCostService?.getByProjectAndCategory(id, 'construction');
    setConstructionCosts(updatedCosts || []);
    setShowAddDivisionModal(false);
    setSelectedDivisions([]);
    setDivisionSearch('');
    setDivisionToast('');
    setAddDivisionLoading(false);
  };

  const handleDeleteDivision = async (division) => {
    setOpenDivisionMenu(null);
    try {
      const count = constructionCosts?.filter(item => extractDivisionCode(item?.costCode) === division?.code)?.length ?? 0;
      if (count > 0) {
        setBlockedDivisionCostCount(count);
        setShowDeleteDivisionBlockedModal(true);
        return;
      }
      setPendingDeleteDivision(division);
      setShowDeleteDivisionConfirmModal(true);
    } catch (error) {
      console.error('Error checking division:', error);
    }
  };

  // Add this block - handleConfirmDeleteDivision function
  const handleConfirmDeleteDivision = async () => {
    if (!pendingDeleteDivision) return;
    try {
      setDeletingDivision(true);
      await divisionsService?.remove(pendingDeleteDivision?.id);
      // Re-fetch divisions and costs
      const updatedDivisions = await divisionsService?.getByProjectId(id);
      const normalizedDivisions = (updatedDivisions || [])?.map(div => ({
        ...div,
        code: normalizeDivisionCode(div?.code) ?? div?.code
      }));
      setDivisions(normalizedDivisions);
      const updatedCosts = await projectCostService?.getByProjectAndCategory(id, 'construction');
      setConstructionCosts(updatedCosts || []);
      setShowDeleteDivisionConfirmModal(false);
      setPendingDeleteDivision(null);
    } catch (error) {
      console.error('Error deleting division:', error);
    } finally {
      setDeletingDivision(false);
    }
  };

  // Render division header row
  const renderDivisionHeader = (division, rows) => {
    const isExpanded = isDivisionExpanded(division?.code);
    const sub = calcGroupSubtotals(rows);
    const { pl, plPercent } = sub;
    const isSpecial = division?.id === '__ungrouped__';

    return (
      <React.Fragment key={`div-${division?.code}`}>
        {/* Division Header Row — contains label + inline totals */}
        <tr
          className="border-b border-gray-200 cursor-pointer select-none hover:bg-gray-50 transition-colors h-[34px]"
          onClick={() => toggleDivisionExpanded(division?.code)}
        >
          {/* Left: chevron + division label + item count */}
          <td className="px-4 align-middle" colSpan={2}>
            <div className="flex items-center gap-1.5 h-full">
          {/* Chevron */}
              <span
                className="inline-flex items-center justify-center text-gray-400 transition-transform duration-150 ease-in-out flex-shrink-0"
                style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </span>
              {isSpecial ? (
                <span className="text-gray-500 italic text-xs font-semibold whitespace-nowrap overflow-hidden text-ellipsis">{t('budget.division.ungrouped')}</span>
              ) : (
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="transition-colors duration-150 text-xs font-normal whitespace-nowrap overflow-hidden text-ellipsis"
                    style={{ color: isExpanded ? '#FF6B2C' : '#374151' }}
                  >
                    {t('budget.division.label')} {division?.code} — {division?.name}
                  </span>
                  <span className="flex-shrink-0 text-xs text-gray-400 whitespace-nowrap">({rows?.length} {t('budget.division.items')})</span>
                </div>
              )}
            </div>
          </td>
          {/* Inline totals aligned with table columns */}
          <td className="px-4 align-middle text-right text-xs font-semibold text-gray-700">
            <div className="flex items-center justify-end h-full">{formatCurrency(sub?.budget)}</div>
          </td>
          <td className="px-4 align-middle text-right text-xs font-semibold text-gray-500">
            <div className="flex items-center justify-end h-full">{formatCurrency(sub?.commitmentActual)}</div>
          </td>
          <td className="px-4 align-middle text-right text-xs font-semibold text-gray-500">
            <div className="flex items-center justify-end h-full">{formatCurrency(sub?.expensesActual)}</div>
          </td>
          <td className="px-4 align-middle text-right text-xs font-semibold text-gray-700">
            <div className="flex items-center justify-end h-full">{formatCurrency(sub?.forecast)}</div>
          </td>
          <td className="px-4 align-middle text-right">
            <div className="flex items-center justify-end h-full">
              <span className={`text-xs font-semibold ${getProfitColor(pl)}`}>
                {formatCurrency(Math.abs(pl))}
              </span>
            </div>
          </td>
          <td className="px-4 align-middle text-right">
            <div className="flex items-center justify-end h-full">
              <span className={`text-xs font-semibold ${getMarginColor(plPercent)}`}>
                {plPercent?.toFixed(1)}%
              </span>
            </div>
          </td>
          {/* Actions cell: show inline "+" only when expanded */}
          <td className="px-4 align-middle text-right" style={{ position: 'relative' }}>
            <div className="flex items-center justify-end gap-1 h-full">
              {isExpanded && !isSpecial && (
                <button
                  onClick={(e) => handleOpenAddCostCodeModal(division, e)}
                  title={t('budget.tooltips.addCostCodeToDivision')}
                  style={{ color: '#FF6B2C', transition: 'all 150ms ease' }}
                  className="inline-flex items-center justify-center w-6 h-6 rounded hover:bg-orange-50 cursor-pointer"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="18" />
                    <line x1="6" y1="12" x2="18" y2="12" />
                  </svg>
                </button>
              )}
              {!isSpecial && (
                <div style={{ position: 'relative' }} onClick={(e) => e?.stopPropagation()}>
                  <button
                    ref={(el) => { divisionMenuBtnRefs.current[division?.id] = el; }}
                    onClick={(e) => {
                      e?.stopPropagation();
                      setOpenDivisionMenu(prev => prev === division?.id ? null : division?.id);
                    }}
                    title={t('budget.tooltips.divisionActions')}
                    className="inline-flex items-center justify-center w-6 h-6 rounded hover:bg-gray-200 cursor-pointer text-gray-500 hover:text-gray-700"
                    style={{ fontSize: '16px', lineHeight: 1 }}
                  >
                    ⋯
                  </button>
                  <PortalDropdown
                    anchorRef={{ current: divisionMenuBtnRefs?.current?.[division?.id] }}
                    open={openDivisionMenu === division?.id}
                    onClose={() => setOpenDivisionMenu(null)}
                    width={160}
                    items={[
                      {
                        label: t('budget.division.editDivision'),
                        icon: 'Pencil',
                        onClick: () => {
                          setEditingDivision(division);
                          setEditDivisionForm({ number: division?.code ?? '', title: division?.name ?? '' });
                          setEditDivisionError('');
                          setShowEditDivisionModal(true);
                        },
                      },
                      {
                        label: t('budget.division.deleteDivision'),
                        icon: 'Trash2',
                        onClick: () => handleDeleteDivision(division),
                        variant: 'danger',
                      },
                    ]}
                  />
                </div>
              )}
            </div>
          </td>
        </tr>
        {/* Child rows — cascade indented, no subtotal row */}
        {isExpanded && rows?.map(item => renderCostCodeRow(item, true))}
      </React.Fragment>
    );
  };

  const currentCostCodes = getFilteredCostCodes();
  const shouldGroupByDivision = activeTab === 'construction';
  const divisionGroups = shouldGroupByDivision ? groupCostCodesByDivision(currentCostCodes, divisions) : null;

  return (
    <div className="pt-0 pb-0 px-0">
      {/* Contract creation toast */}
      {contractToast && (
        <div
          className={`fixed bottom-24 left-1/2 z-50 px-4 py-2 rounded-xl shadow-lg flex items-center gap-2 text-sm font-medium text-white ${contractToast?.type === 'success' ? 'bg-green-600' : 'bg-red-500'}`}
          style={{ transform: 'translateX(-50%)', animation: 'feedbackSlideUp 0.25s ease-out forwards' }}
        >
          <Icon name={contractToast?.type === 'success' ? 'CheckCircle2' : 'XCircle'} size={15} />
          {contractToast?.message}
          <style>{`
            @keyframes feedbackSlideUp {
              from { opacity: 0; transform: translateX(-50%) translateY(10px); }
              to   { opacity: 1; transform: translateX(-50%) translateY(0); }
            }
          `}</style>
        </div>
      )}
      {/* Header Block with KPI Cards on Same Row */}
      <div className="mb-4">
        <div className="flex items-start justify-between gap-6">
          {/* Left Side: Breadcrumb Navigation */}
          <div className="flex-shrink-0">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="DollarSign" size={20} className="text-primary" />
              <h1 className="text-lg font-semibold text-[#2C3E50]">
                {t('budget.title')}
              </h1>
            </div>
            <div className="flex items-center gap-1 mb-1">
              <span
                onClick={() => navigate('/project-detail')}
                className="text-sm text-primary font-normal hover:text-primary-dark cursor-pointer transition-colors"
              >
                {project?.projectNumber || t('common.na')}
              </span>
              <span className="text-sm text-primary font-normal">-</span>
              <span
                onClick={() => navigate('/project-detail')}
                className="text-sm text-primary font-normal hover:text-primary-dark cursor-pointer transition-colors"
              >
                {project?.name || t('common.loading')}
              </span>
            </div>
            <p className="text-xs text-gray-500">
              {t('budget.subtitle')}
            </p>
          </div>

          {/* Right Side: KPI Cards */}
          <div className="flex-1 flex items-center justify-end gap-2">
            <StatsCard
              title={t('budget.kpi.initialBudget')}
              value={formatCurrency(kpis?.totalContract || 0)}
              sublabel=""
              iconName="DollarSign"
              iconColor="text-blue-500"
              valueColor="text-blue-500"
              titleWeight="font-medium"
            />
            <StatsCard
              title={t('budget.kpi.extraCredit')}
              value={`${formatCurrency(kpis?.netChange ?? 0)}`}
              sublabel=""
              iconName="DollarSign"
              iconColor="text-purple-600"
              valueColor="text-purple-600"
              titleWeight="font-medium"
            />
            <StatsCard
              title={t('budget.kpi.totalActualCost')}
              value={`${formatCurrency(kpis?.totalActualCost ?? 0)}`}
              sublabel=""
              iconName="TrendingDown"
              iconColor="text-orange-500"
              valueColor="text-gray-900"
              titleWeight="font-medium"
            />
            <StatsCard
              title={t('budget.kpi.liveProfit')}
              value={`${formatCurrency(kpis?.liveProfit ?? 0)}`}
              sublabel=""
              iconName={kpis?.liveProfit >= 0 ? 'TrendingUp' : 'TrendingDown'}
              iconColor={kpis?.liveProfit >= 0 ? 'text-green-500' : 'text-red-500'}
              valueColor={kpis?.liveProfit >= 0 ? 'text-green-600' : 'text-red-600'}
              titleWeight="font-semibold"
            />
            <StatsCard
              title={t('budget.kpi.profitMargin')}
              value={`${(kpis?.profitMargin ?? 0)?.toFixed(1)}%`}
              sublabel=""
              iconName="Percent"
              iconColor="text-purple-500"
              valueColor={(kpis?.profitMargin ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}
              titleWeight="font-medium"
            />
          </div>
        </div>
      </div>
      {/* Tab Navigation with Filter and Add Button */}
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-white rounded-lg border border-gray-200 p-1 shadow-sm">
              {tabs?.map(tab => (
                <button
                  key={tab?.id}
                  onClick={() => { setActiveTab(tab?.id); setSelectedCostCodeIds(new Set()); }}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200 ${
                    activeTab === tab?.id
                      ? 'bg-primary text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {tab?.label}
                </button>
              ))}
            </div>

            {/* Apple-style inline filter */}
            <div className="relative">
              <button
                onClick={() => setFilterDropdownOpen(!filterDropdownOpen)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all duration-150 ${
                  activeFilter !== 'all' ?'text-primary hover:bg-orange-50' :'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <div className="relative">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                  </svg>
                  {activeFilter !== 'all' && (
                    <span className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-primary rounded-full" />
                  )}
                </div>
                {activeFilter !== 'all' && (
                  <span className="text-primary">
                    {filterOptions?.find(f => f?.id === activeFilter)?.label}
                  </span>
                )}
              </button>
              {activeFilter !== 'all' && (
                <button
                  onClick={() => applyFilter('all')}
                  className="inline-flex items-center justify-center w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors ml-0.5"
                  title={t('budget.tooltips.clearFilter')}
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
              {filterDropdownOpen && (
                <div className="absolute left-0 top-9 z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-52">
                  {filterOptions?.map(option => (
                    <button
                      key={option?.id}
                      onClick={() => applyFilter(option?.id)}
                      className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 transition-colors ${
                        activeFilter === option?.id
                          ? 'text-primary font-medium bg-orange-50'
                          : 'text-gray-700'
                      }`}
                    >
                      {option?.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <button
onClick={() => {
  if (activeTab === 'construction') {
    setSelectedDivisions([]);
    setDivisionSearch('');
    setDivisionToast('');
    setShowAddDivisionModal(true);
  } else {
    setShowAddGeneralCostCodeModal(true);
  }
}}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-[#FF5A1A] rounded-lg transition-colors shadow-sm ${activeTab === 'extras' ? 'invisible' : ''}`}
          >
            <Icon name="Plus" size={13} />
            {activeTab === 'construction' ? t('budget.addDivision.title') : t('budget.addCostCodeModal.title')}
          </button>
        </div>
      </div>
      {/* Content Wrapper */}
      <div className="space-y-4">
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          {activeTab === 'extras' ? (
            /* Extras & Credits Tab */
            // Construction budget view only displays approved extras and credits
            // negotiation statuses remain visible in the ExtrasPage
            (approvedExtrasCosts?.length === 0 && !loading ? (<div className="p-12 text-center">
              <Icon name="FolderOpen" size={40} className="mx-auto mb-3 text-gray-300" />
              <p className="text-sm text-gray-500">{t('budget.empty.emptyTitle')}</p>
              <p className="text-xs text-gray-400 mt-1">{t('budget.empty.emptyDescription')}</p>
            </div>) : (<div className="max-h-[550px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr className="border-b border-gray-200">
                    <th className="text-left text-xs font-semibold text-gray-700 px-4 py-2.5 align-middle whitespace-nowrap w-[80px]">
                      <span>{t('budget.table.type')}</span>
                    </th>
                    <th className="text-left text-xs font-semibold text-gray-700 px-4 py-2.5 align-middle whitespace-nowrap w-[80px]">
                      <span>{t('budget.table.coNumber')}</span>
                    </th>
                    <th className="text-left text-xs font-semibold text-gray-700 px-4 py-2.5 align-middle whitespace-nowrap">
                      <span>{t('budget.table.description')}</span>
                    </th>
                    <th className="text-right text-xs font-semibold text-gray-700 px-4 py-2.5 align-middle whitespace-nowrap w-[10%] min-w-[120px]">
                      {t('budget.table.initialValue') || 'Initial Value'}
                    </th>
                    <th className="text-right text-xs font-semibold text-gray-700 px-4 py-2.5 align-middle whitespace-nowrap w-[10%]">
                      {t('budget.table.commitment')}
                    </th>
                    <th className="text-right text-xs font-semibold text-gray-700 px-4 py-2.5 align-middle whitespace-nowrap w-[10%]">
                      {t('budget.table.expenses')}
                    </th>
                    <th className="text-right text-xs font-semibold text-gray-700 px-4 py-2.5 align-middle whitespace-nowrap w-[12%]">
                      {t('budget.table.forecast')}
                    </th>
                    <th className="text-right text-xs font-semibold text-gray-700 px-4 py-2.5 align-middle whitespace-nowrap w-[12%]">
                      {t('budget.table.pl')}
                    </th>
                    <th className="text-right text-xs font-semibold text-gray-700 px-4 py-2.5 align-middle whitespace-nowrap w-[10%]">
                      {t('budget.table.plPercent')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 5 })?.map((_, index) => (
                      <tr key={`skeleton-extras-${index}`} className="border-b border-gray-100">
                        <td className="px-4 py-1.5"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
                        <td className="px-4 py-1.5"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
                        <td className="px-4 py-1.5"><div className="h-4 bg-gray-200 rounded w-48"></div></td>
                        <td className="px-4 py-1.5 text-right"><div className="h-4 bg-gray-200 rounded w-24 ml-auto"></div></td>
                        <td className="px-4 py-1.5 text-right"><div className="h-4 bg-gray-200 rounded w-24 ml-auto"></div></td>
                        <td className="px-4 py-1.5 text-right"><div className="h-4 bg-gray-200 rounded w-24 ml-auto"></div></td>
                        <td className="px-4 py-1.5 text-right"><div className="h-4 bg-gray-200 rounded w-24 ml-auto"></div></td>
                        <td className="px-4 py-1.5 text-right"><div className="h-4 bg-gray-200 rounded w-20 ml-auto"></div></td>
                      </tr>
                    ))
                  ) : (
                    approvedExtrasCosts?.map(item => {
                      const isExpanded = expandedExtras?.[item?.id];
                      const lines = extraLinesCache?.[item?.id] || [];
                      const isLoadingLines = extraLinesLoading?.[item?.id];
                      return (
                        <React.Fragment key={item?.id}>
                          <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-1.5 align-middle whitespace-nowrap text-xs font-medium text-gray-700" style={{ width: '80px' }}>
                              <div className="flex items-center gap-1.5">
                                <button
                                  onClick={() => toggleExtraExpanded(item?.id)}
                                  className="inline-flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors flex-shrink-0"
                                  title={isExpanded ? t('budget.tooltips.collapse') : t('budget.tooltips.expand')}
                                >
                                  <span
                                    className="inline-flex items-center justify-center transition-transform duration-150 ease-in-out"
                                    style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                                  >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="9 18 15 12 9 6" />
                                    </svg>
                                  </span>
                                </button>
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                  item?.type === 'extra' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                }`}>
                                  {item?.type === 'extra' ? 'Extra' : 'Credit'}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-1.5 align-middle whitespace-nowrap text-xs font-medium text-gray-700" style={{ width: '80px' }}>
                              <div className="flex items-center h-full">
                                {item?.coNumber || item?.co_number || '—'}
                              </div>
                            </td>
                            <td className="px-4 py-1.5 align-middle max-w-[400px]">
                              <div className="flex items-center h-full">
                                <span className="text-xs text-gray-600 whitespace-nowrap overflow-hidden text-ellipsis block">{item?.title || '—'}</span>
                              </div>
                            </td>
                            <td className="px-4 py-1.5 align-middle text-xs text-gray-900 text-right font-medium">
                              <div className="flex items-center justify-end h-full">{formatCurrency(item?.initialValue || 0)}</div>
                            </td>
                            <td className="px-4 py-1.5 align-middle text-xs text-gray-500 text-right">
                              <div className="flex items-center justify-end h-full">{formatCurrency(item?.commitment || 0)}</div>
                            </td>
                            <td className="px-4 py-1.5 align-middle text-xs text-gray-500 text-right">
                              <div className="flex items-center justify-end h-full">{formatCurrency(item?.expenses || 0)}</div>
                            </td>
                            <td className="px-4 py-1.5 align-middle text-xs text-right">
                              <div className="flex items-center justify-end h-full">
                              <input
                                type="text"
                                inputMode="numeric"
                                value={item?.forecast ?? ""}
                                onFocus={(e) => e?.target?.select()}
                                onChange={(e) => handleUpdateForecast(item?.id, e?.target?.value)}
                                onBlur={(e) => handleUpdateForecast(item?.id, e?.target?.value, true)}
                                className="w-20 h-6 block ml-auto text-right text-xs border border-gray-200 rounded px-1 leading-none outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white"
                              />
                              </div>
                            </td>
                            <td className="px-4 py-1.5 align-middle text-xs text-right">
                              <div className="flex items-center justify-end h-full">
                                <span className={`font-semibold ${getProfitColor(item?.pl || 0)}`}>
                                  {formatCurrency(Math.abs(item?.pl || 0))}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-1.5 align-middle text-xs text-right">
                              <div className="flex items-center justify-end h-full">
                                {item?.pl != null && item?.initialValue
                                  ? <span className={`font-semibold ${getMarginColor((item?.pl / Math.abs(item?.initialValue)) * 100)}`}>{`${((item?.pl / Math.abs(item?.initialValue)) * 100)?.toFixed(1)}%`}</span>
                                  : <span className="text-gray-400">—</span>}
                              </div>
                            </td>
                          </tr>
                          {/* Expanded extra lines */}
                          {isExpanded && (
                            isLoadingLines ? (
                              <tr className="border-b border-gray-100">
                                <td colSpan={9} className="pl-10 pr-4 py-1">
                                  <div className="flex items-center gap-2 text-xs text-gray-400 leading-tight">
                                    <div className="w-3 h-3 border border-gray-300 border-t-transparent rounded-full animate-spin" />
                                    {t('budget.extras.loadingLines')}
                                  </div>
                                </td>
                              </tr>
                            ) : lines?.length === 0 ? (
                              <tr className="border-b border-gray-100">
                                <td colSpan={9} className="pl-10 pr-4 py-1 text-xs text-gray-400 italic leading-tight">
                                  {t('budget.extras.noLines')}
                                </td>
                              </tr>
                            ) : (
                              lines?.map(line => {
                                const internalLabel = orgName ? `${orgName} ${t('internal.suffix')}` : '';
                                const subName = line?.is_internal
                                  ? internalLabel
                                  : line?.subcontracts?.subcontractors?.company_name || internalLabel;
                                return (
                                  <tr key={line?.id} className="border-b border-gray-100">
                                    <td className="px-4 py-1 align-middle" />
                                    <td className="px-4 py-1 align-middle" />
                                    <td className="pl-10 pr-4 py-1 align-middle text-xs text-muted-foreground font-normal leading-tight">
                                      <div className="flex items-center h-full">
                                        {line?.subcontractor_name || subName}
                                      </div>
                                    </td>
                                    <td className="px-4 py-1 align-middle" />
                                    <td className="px-4 py-1 align-middle text-xs text-muted-foreground font-normal text-right leading-tight">
                                      <div className="flex items-center justify-end h-full">
                                        {formatCurrency(line?.commitment || 0)}
                                      </div>
                                    </td>
                                    <td className="px-4 py-1 align-middle" />
                                    <td className="px-4 py-1 align-middle" />
                                    <td className="px-4 py-1 align-middle" />
                                    <td className="px-4 py-1 align-middle" />
                                  </tr>
                                );
                              })
                            )
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>))
          ) : null}
          {activeTab !== 'extras' && (
            !loading && (
              (activeTab === 'construction' && divisions?.length === 0) ||
              (activeTab === 'general' && generalCosts?.length === 0) ||
              (activeTab === 'admin' && adminCosts?.length === 0)
            ) ? (
              <div className="p-12 text-center">
                <Icon name="FolderOpen" size={40} className="mx-auto mb-3 text-gray-300" />
                <p className="text-sm text-gray-500">{t('budget.empty.emptyTitle')}</p>
                <p className="text-xs text-gray-400 mt-1">{t('budget.empty.emptyDescription')}</p>
              </div>
            ) : (
              <div className="max-h-[550px] overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr className="border-b border-gray-200">
                                            <th className="text-left text-xs font-semibold text-gray-700 px-4 py-2.5 align-middle whitespace-nowrap w-[37%]" colSpan={2}>
                        {shouldGroupByDivision && (() => {
                          const allExpanded = divisions?.length > 0 &&
                            divisions?.every(div => isDivisionExpanded(div?.code));
                          return (
                            <button
                              onClick={toggleAllDivisions}
                              className="inline-flex items-center justify-center w-4 h-4 mr-1.5 text-gray-400 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/20"
                              title={allExpanded ? t('budget.tooltips.collapseAll') : t('budget.tooltips.expandAll')}
                            >
                              <span
                                className="inline-flex items-center justify-center text-gray-400 transition-transform duration-150 ease-in-out flex-shrink-0"
                                style={{ transform: allExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="9 18 15 12 9 6" />
                                </svg>
                              </span>
                            </button>
                          );
                        })()}
                        <span>{t('budget.table.description')}</span>
                      </th>
                      <th className="text-right text-xs font-semibold text-gray-700 px-4 py-2.5 align-middle whitespace-nowrap w-[10%]">
                        {t('budget.table.budget')}
                      </th>
                      <th className="text-right text-xs font-semibold text-gray-700 px-4 py-2.5 align-middle whitespace-nowrap w-[10%]">
                        {t('budget.table.commitment')}
                      </th>
                      <th className="text-right text-xs font-semibold text-gray-700 px-4 py-2.5 align-middle whitespace-nowrap w-[10%]">
                        {t('budget.table.expenses')}
                      </th>
                      <th className="text-right text-xs font-semibold text-gray-700 px-4 py-2.5 align-middle whitespace-nowrap w-[12%]">
                        {t('budget.table.forecast')}
                      </th>
                      <th className="text-right text-xs font-semibold text-gray-700 px-4 py-2.5 align-middle whitespace-nowrap w-[12%]">
                        {t('budget.table.pl')}
                      </th>
                      <th className="text-right text-xs font-semibold text-gray-700 px-4 py-2.5 align-middle whitespace-nowrap w-[10%]">
                        {t('budget.table.plPercent')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && currentCostCodes?.length === 0 ? (
                      (Array.from({ length: 5 })?.map((_, index) => (
                        <tr key={`skeleton-${index}`} className="border-b border-gray-100">
                          <td className="px-4 py-1.5"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
                          <td className="px-4 py-1.5"><div className="h-4 bg-gray-200 rounded w-48"></div></td>
                          <td className="px-4 py-1.5 text-right"><div className="h-4 bg-gray-200 rounded w-24 ml-auto"></div></td>
                          <td className="px-4 py-1.5 text-right"><div className="h-4 bg-gray-200 rounded w-24 ml-auto"></div></td>
                          <td className="px-4 py-1.5 text-right"><div className="h-4 bg-gray-200 rounded w-24 ml-auto"></div></td>
                          <td className="px-4 py-1.5 text-right"><div className="h-4 bg-gray-200 rounded w-20 ml-auto"></div></td>
                        </tr>
                      )))
                    ) : shouldGroupByDivision && divisionGroups !== null ? (
                      (divisionGroups?.map(({ division, rows }) =>
                        renderDivisionHeader(division, rows)
                      ))
                    ) : (
                      (currentCostCodes?.map((item) => renderCostCodeRow(item, false)))
                    )}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      </div>
      {/* Add Division Modal */}
      {showAddDivisionModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backdropFilter: 'blur(4px)', backgroundColor: 'rgba(0,0,0,0.25)' }}
          onClick={(e) => { if (e?.target === e?.currentTarget) { setShowAddDivisionModal(false); setAddDivisionForm({ number: '', title: '' }); setAddDivisionError(''); setDivisionSearch(''); setDivisionToast(''); setSelectedDivisions([]); } }}
        >
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 pt-5 pb-4 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-900">{t('budget.addDivision.title')}</h2>
            </div>
            {/* Search Bar */}
            <div className="px-6 pt-4 pb-2">
              <input
                type="text"
                value={divisionSearch}
                onChange={(e) => setDivisionSearch(e?.target?.value)}
                placeholder={t('budget.addDivision.searchPlaceholder')}
                autoFocus
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
              />
            </div>
            {/* Toast */}
            {divisionToast && (
              <div className="mx-6 mb-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700 font-medium">
                {divisionToast}
              </div>
            )}
            {/* Scrollable Division List */}
            <div className="max-h-[340px] overflow-y-auto px-3 pb-3">
              {CSI_DIVISIONS
                ?.filter(div => div?.number !== '00' && div?.number !== '01')
                ?.filter(div =>
                  divisionSearch === '' ||
                  div?.number?.includes(divisionSearch) ||
                  div?.title?.toLowerCase()?.includes(divisionSearch?.toLowerCase())
                )
                ?.map(division => {
                  const normalizedCode = normalizeDivisionCode(division?.number);
                  const alreadyAdded = divisions?.some(d => normalizeDivisionCode(d?.code) === normalizedCode);
                  const isSelected = selectedDivisions?.some(d => d?.number === division?.number);
                  return (
                    <button
                      key={division?.number}
                      onClick={() => { if (!alreadyAdded) toggleDivision(division); }}
                      disabled={alreadyAdded || addDivisionLoading}
                      className={`w-full text-left flex items-center justify-between px-4 py-2 rounded-md text-xs transition-colors ${
                        alreadyAdded
                          ? 'text-gray-400 cursor-not-allowed'
                          : isSelected
                            ? 'bg-orange-50 text-orange-600' :'hover:bg-gray-100 cursor-pointer text-gray-800'
                      }`}
                    >
                      <span>
                        <span className="font-medium">{t('budget.addDivision.divisionLabel', { number: division?.number })}</span>
                        <span className={alreadyAdded ? 'text-gray-400' : isSelected ? 'text-orange-400' : 'text-gray-500'}> — </span>
                        <span>{division?.title}</span>
                        {alreadyAdded && <span className="ml-2 text-gray-400 text-[10px]">{t('budget.addDivision.alreadyAdded')}</span>}
                      </span>
                      {isSelected && !alreadyAdded && (
                        <Icon name="Check" size={14} className="text-orange-600 flex-shrink-0 ml-2" />
                      )}
                    </button>
                  );
                })
              }
              {CSI_DIVISIONS?.filter(div => div?.number !== '00' && div?.number !== '01')?.filter(div =>
                divisionSearch === '' ||
                div?.number?.includes(divisionSearch) ||
                div?.title?.toLowerCase()?.includes(divisionSearch?.toLowerCase())
              )?.length === 0 && (
                <p className="px-4 py-3 text-xs text-gray-400 text-center">{t('budget.addDivision.noDivisionsFound')}</p>
              )}
            </div>
            {/* Modal Footer */}
            <div className="px-6 pb-5 pt-3 border-t border-gray-100 flex items-center justify-end gap-2">
              <button
                onClick={() => { setShowAddDivisionModal(false); setAddDivisionForm({ number: '', title: '' }); setAddDivisionError(''); setDivisionSearch(''); setDivisionToast(''); setSelectedDivisions([]); }}
                className="px-4 py-2 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleAddSelectedDivisions}
                disabled={selectedDivisions?.length === 0 || addDivisionLoading}
                className={`px-4 py-2 text-xs font-medium rounded-md transition-colors ${
                  selectedDivisions?.length === 0 || addDivisionLoading
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed' :'bg-primary text-white hover:bg-primary-dark'
                }`}
              >
                {addDivisionLoading ? t('budget.addDivision.adding') : t('budget.addDivision.addToProject')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Add Cost Code Modal (inline division context) */}
      {showAddCostCodeModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backdropFilter: 'blur(4px)', backgroundColor: 'rgba(0,0,0,0.25)' }}
          onClick={(e) => { if (e?.target === e?.currentTarget) { setShowAddCostCodeModal(false); setAddCostCodeDivision(null); setAddCostCodeForm({ costCode: '', description: '', budget: '', forecast: '' }); } }}
        >
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 pt-5 pb-4 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-900">{t('budget.addCostCodeModal.title')}</h2>
              {addCostCodeDivision && (
                <p className="text-xs text-gray-500 mt-0.5">
                  {t('budget.division.label')} {addCostCodeDivision?.code} – {addCostCodeDivision?.name}
                </p>
              )}
            </div>
            {/* Modal Body */}
            <div className="px-6 py-5 space-y-4">
              {/* Division field — locked/readonly */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  {t('budget.addCostCodeModal.division')}
                </label>
                <input
                  type="text"
                  value={addCostCodeDivision ? `${addCostCodeDivision?.code} – ${addCostCodeDivision?.name}` : ''}
                  readOnly
                  disabled
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                />
              </div>
              {/* Cost Code */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  {t('budget.addCostCodeModal.costCode')}
                </label>
                <input
                  type="text"
                  value={addCostCodeForm?.costCode}
                  onChange={(e) => setAddCostCodeForm(prev => ({ ...prev, costCode: e?.target?.value }))}
                  placeholder={addCostCodeDivision?.code ? `${addCostCodeDivision?.code}010` : '02010'}
                  autoFocus
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                />
              </div>
              {/* Description */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  {t('budget.addCostCodeModal.description')}
                </label>
                <input
                  type="text"
                  value={addCostCodeForm?.description}
                  onChange={(e) => setAddCostCodeForm(prev => ({ ...prev, description: e?.target?.value }))}
                  placeholder={t('budget.addCostCodeModal.descriptionPlaceholder')}
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                  onKeyDown={(e) => { if (e?.key === 'Enter') handleSaveAddCostCode(); }}
                />
              </div>
              {/* Budget */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  {t('budget.table.budget')}
                </label>
                <input
                  type="number"
                  step="0.01"
                  inputMode="decimal"
                  value={addCostCodeForm?.budget}
                  onChange={(e) => setAddCostCodeForm(prev => ({ ...prev, budget: e?.target?.value }))}
                  placeholder="0.00"
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                />
              </div>
            </div>
            {/* Modal Footer */}
            <div className="px-6 pb-5 flex items-center justify-end gap-2">
              <button
                onClick={() => { setShowAddCostCodeModal(false); setAddCostCodeDivision(null); setAddCostCodeForm({ costCode: '', description: '', budget: '', forecast: '' }); }}
                className="px-4 py-2 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSaveAddCostCode}
                disabled={!addCostCodeForm?.description || addCostCodeLoading}
                className="px-4 py-2 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addCostCodeLoading ? t('common.creating') : t('budget.addCostCodeModal.submit')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Add Cost Code Modal (general / admin tabs — no division) */}
      {showAddGeneralCostCodeModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backdropFilter: 'blur(4px)', backgroundColor: 'rgba(0,0,0,0.25)' }}
          onClick={(e) => { if (e?.target === e?.currentTarget) { setShowAddGeneralCostCodeModal(false); setAddGeneralCostCodeForm({ costCode: '', description: '' }); } }}
        >
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 pt-5 pb-4 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-900">{t('budget.addCostCodeModal.title')}</h2>
            </div>
            {/* Modal Body */}
            <div className="px-6 py-5 space-y-4">
              {/* Cost Code */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  {t('budget.addCostCodeModal.costCode')}
                </label>
                <input
                  type="text"
                  value={addGeneralCostCodeForm?.costCode}
                  onChange={(e) => setAddGeneralCostCodeForm(prev => ({ ...prev, costCode: e?.target?.value }))}
                  placeholder="02010"
                  autoFocus
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                />
              </div>
              {/* Description */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  {t('budget.addCostCodeModal.description')}
                </label>
                <input
                  type="text"
                  value={addGeneralCostCodeForm?.description}
                  onChange={(e) => setAddGeneralCostCodeForm(prev => ({ ...prev, description: e?.target?.value }))}
                  placeholder={t('budget.addCostCodeModal.descriptionPlaceholder')}
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                  onKeyDown={(e) => { if (e?.key === 'Enter') handleSaveGeneralAdminCostCode(); }}
                />
              </div>
            </div>
            {/* Modal Footer */}
            <div className="px-6 pb-5 flex items-center justify-end gap-2">
              <button
                onClick={() => { setShowAddGeneralCostCodeModal(false); setAddGeneralCostCodeForm({ costCode: '', description: '' }); }}
                className="px-4 py-2 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSaveGeneralAdminCostCode}
                disabled={!addGeneralCostCodeForm?.description || addGeneralCostCodeLoading}
                className="px-4 py-2 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addGeneralCostCodeLoading ? t('common.creating') : t('budget.addCostCodeModal.submit')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Delete Confirm Modal — CASE A */}
      {showDeleteConfirmModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backdropFilter: 'blur(4px)', backgroundColor: 'rgba(0,0,0,0.25)' }}
          onClick={(e) => { if (e?.target === e?.currentTarget) { setShowDeleteConfirmModal(false); setPendingDeleteId(null); } }}
        >
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 overflow-hidden">
            <div className="px-6 pt-5 pb-4 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-900">{t('budget.deleteCostCode.confirmTitle')}</h2>
            </div>
            <div className="px-6 py-5">
              <p className="text-xs text-gray-600">{t('budget.deleteCostCode.confirmMessage')}</p>
            </div>
            <div className="px-6 pb-5 flex items-center justify-end gap-2">
              <button
                onClick={() => { setShowDeleteConfirmModal(false); setPendingDeleteId(null); }}
                className="px-4 py-2 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors shadow-sm"
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Delete Blocked Modal — CASE B */}
      {showDeleteBlockedModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backdropFilter: 'blur(4px)', backgroundColor: 'rgba(0,0,0,0.25)' }}
          onClick={(e) => { if (e?.target === e?.currentTarget) setShowDeleteBlockedModal(false); }}
        >
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 pt-5 pb-4 border-b border-gray-100 flex items-center gap-2">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-red-100 flex items-center justify-center">
                <Icon name="AlertCircle" size={15} className="text-red-600" />
              </div>
              <h2 className="text-sm font-semibold text-gray-900">{t('budget.deleteCostCode.blockedTitle')}</h2>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-xs text-gray-600">{t('budget.deleteCostCode.blockedMessage')}</p>
              {deleteBlockedInfo?.contracts?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-1.5">{t('budget.deleteCostCode.linkedContracts')} ({deleteBlockedInfo?.contracts?.length})</p>
                  <ul className="space-y-1">
                    {deleteBlockedInfo?.contracts?.map((c, i) => (
                      <li key={i} className="flex items-center justify-between gap-2 text-xs text-gray-700 bg-gray-50 rounded px-3 py-1.5">
                        <span className="flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
                          {c?.name}
                        </span>
                        <span className="font-medium text-gray-900 flex-shrink-0">{formatCurrency(c?.value || 0)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {deleteBlockedInfo?.invoices?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-1.5">{t('budget.deleteCostCode.linkedInvoices')} ({deleteBlockedInfo?.invoices?.length})</p>
                  <ul className="space-y-1">
                    {deleteBlockedInfo?.invoices?.map((inv, i) => (
                      <li key={i} className="flex items-center justify-between gap-2 text-xs text-gray-700 bg-gray-50 rounded px-3 py-1.5">
                        <span className="flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-purple-400 flex-shrink-0" />
                          {t('budget.deleteCostCode.invoiceLabel') || 'Invoice'} #{inv?.number}
                        </span>
                        <span className="font-medium text-gray-900 flex-shrink-0">{formatCurrency(inv?.amount || 0)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {deleteBlockedInfo?.expenses?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-1.5">{t('budget.deleteCostCode.linkedExpenses')} ({deleteBlockedInfo?.expenses?.length})</p>
                  <ul className="space-y-1">
                    {deleteBlockedInfo?.expenses?.map((exp, i) => (
                      <li key={i} className="flex items-center justify-between gap-2 text-xs text-gray-700 bg-gray-50 rounded px-3 py-1.5">
                        <span className="flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-orange-400 flex-shrink-0" />
                          <span className="truncate max-w-[200px]">{exp?.description}</span>
                        </span>
                        <span className="font-medium text-gray-900 flex-shrink-0">{formatCurrency(exp?.amount || 0)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="px-6 pb-5 flex items-center justify-end">
              <button
                onClick={() => setShowDeleteBlockedModal(false)}
                className="px-4 py-2 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Delete Warning Modal — CASE C */}
      {showDeleteWarningModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backdropFilter: 'blur(4px)', backgroundColor: 'rgba(0,0,0,0.25)' }}
          onClick={(e) => { if (e?.target === e?.currentTarget) { setShowDeleteWarningModal(false); setPendingDeleteWarningId(null); } }}
        >
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 overflow-hidden">
            <div className="px-6 pt-5 pb-4 border-b border-gray-100 flex items-center gap-2">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center">
                <Icon name="AlertTriangle" size={15} className="text-amber-600" />
              </div>
              <h2 className="text-sm font-semibold text-gray-900">{t('budget.deleteCostCode.warningTitle')}</h2>
            </div>
            <div className="px-6 py-5">
              <p className="text-xs text-gray-600">{t('budget.deleteCostCode.warningMessage')}</p>
            </div>
            <div className="px-6 pb-5 flex items-center justify-end gap-2">
              <button
                onClick={() => { setShowDeleteWarningModal(false); setPendingDeleteWarningId(null); }}
                className="px-4 py-2 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleConfirmDeleteAnyway}
                className="px-4 py-2 text-xs font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors shadow-sm"
              >
                {t('budget.deleteCostCode.deleteAnyway')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Edit Division Modal */}
      {showEditDivisionModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backdropFilter: 'blur(4px)', backgroundColor: 'rgba(0,0,0,0.25)' }}
          onClick={(e) => { if (e?.target === e?.currentTarget) { setShowEditDivisionModal(false); setEditingDivision(null); setEditDivisionForm({ number: '', title: '' }); setEditDivisionError(''); } }}
        >
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 overflow-hidden">
            <div className="px-6 pt-5 pb-4 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-900">{t('budget.division.editDivision')}</h2>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  {t('budget.addDivision.divisionNumber')}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{1,2}"
                  maxLength={2}
                  value={editDivisionForm?.number}
                  onChange={(e) => { setEditDivisionError(''); setEditDivisionForm(prev => ({ ...prev, number: e?.target?.value?.replace(/[^0-9]/g, '')?.slice(0, 2) })); }}
                  placeholder={t('budget.addDivision.divisionNumberPlaceholder')}
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                />
                {editDivisionError && (
                  <p className="mt-1 text-xs text-red-500">{editDivisionError}</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  {t('budget.addDivision.divisionTitle')}
                </label>
                <input
                  type="text"
                  value={editDivisionForm?.title}
                  onChange={(e) => setEditDivisionForm(prev => ({ ...prev, title: e?.target?.value }))}
                  placeholder={t('budget.addDivision.divisionTitlePlaceholder')}
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                  onKeyDown={(e) => { if (e?.key === 'Enter') handleSaveEditDivision(); }}
                />
              </div>
            </div>
            <div className="px-6 pb-5 flex items-center justify-end gap-2">
              <button
                onClick={() => { setShowEditDivisionModal(false); setEditingDivision(null); setEditDivisionForm({ number: '', title: '' }); setEditDivisionError(''); }}
                className="px-4 py-2 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSaveEditDivision}
                disabled={!editDivisionForm?.number || !editDivisionForm?.title || editDivisionLoading}
                className="px-4 py-2 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editDivisionLoading ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Delete Division Confirm Modal */}
      {showDeleteDivisionConfirmModal && pendingDeleteDivision && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backdropFilter: 'blur(4px)', backgroundColor: 'rgba(0,0,0,0.25)' }}
          onClick={(e) => { if (e?.target === e?.currentTarget && !deletingDivision) { setShowDeleteDivisionConfirmModal(false); setPendingDeleteDivision(null); } }}
        >
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 overflow-hidden">
            <div className="px-6 pt-5 pb-4 border-b border-gray-100 flex items-center gap-2">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-red-100 flex items-center justify-center">
                <Icon name="Trash2" size={15} className="text-red-600" />
              </div>
              <h2 className="text-sm font-semibold text-gray-900">{t('budget.division.deleteDivision')}</h2>
            </div>
            <div className="px-6 py-5 space-y-3">
              <p className="text-xs text-gray-600">{t('budget.division.deleteConfirmMessage')}</p>
              <div className="bg-gray-50 rounded-lg px-4 py-3">
                <p className="text-xs font-medium text-gray-800">{pendingDeleteDivision?.code} – {pendingDeleteDivision?.name}</p>
              </div>
              <p className="text-xs text-red-500">{t('budget.division.deleteIrreversible')}</p>
            </div>
            <div className="px-6 pb-5 flex items-center justify-end gap-2">
              <button
                onClick={() => { if (!deletingDivision) { setShowDeleteDivisionConfirmModal(false); setPendingDeleteDivision(null); } }}
                disabled={deletingDivision}
                className="px-4 py-2 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleConfirmDeleteDivision}
                disabled={deletingDivision}
                className="px-4 py-2 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deletingDivision ? t('budget.division.deleting') : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Delete Division Blocked Modal */}
      {showDeleteDivisionBlockedModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backdropFilter: 'blur(4px)', backgroundColor: 'rgba(0,0,0,0.25)' }}
          onClick={(e) => { if (e?.target === e?.currentTarget) setShowDeleteDivisionBlockedModal(false); }}
        >
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 overflow-hidden">
            <div className="px-6 pt-5 pb-4 border-b border-gray-100 flex items-center gap-2">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-red-100 flex items-center justify-center">
                <Icon name="AlertCircle" size={15} className="text-red-600" />
              </div>
              <h2 className="text-sm font-semibold text-gray-900">{t('budget.division.deleteDivision')}</h2>
            </div>
            <div className="px-6 py-5">
              <p className="text-xs text-gray-600">{t('budget.division.cannotDeleteWithCount', { count: blockedDivisionCostCount })}</p>
            </div>
            <div className="px-6 pb-5 flex items-center justify-end">
              <button
                onClick={() => setShowDeleteDivisionBlockedModal(false)}
                className="px-4 py-2 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Cost Code Note Modal */}
      {ENABLE_COST_CODE_NOTES && noteModal && (
        <CostCodeNoteModal
          costCodeId={noteModal?.id}
          costCode={noteModal?.costCode}
          description={noteModal?.description}
          onClose={() => setNoteModal(null)}
          onSaved={(savedNote) => {
            setCostCodeNotes(prev => ({ ...prev, [noteModal?.id]: savedNote }));
            setNoteModal(null);
          }}
        />
      )}
      {/* ── Bulk Action Bar — Create Contract ──────────────────────────────────── */}
      {activeTab === 'construction' && (
        <BulkActionBar
          count={selectedCostCodeIds?.size}
          actions={[
            {
              key: 'createContract',
              label: t('bulk.createContract'),
              icon: 'FilePlus',
              variant: 'primary',
              onClick: handleCreateContracts,
            },
          ]}
          onClear={() => setSelectedCostCodeIds(new Set())}
          clearLabel={t('bulk.clear')}
        />
      )}
    </div>
  );
};

export default BudgetPage;